/**
 * /app/competencies — Competency Management workspace.
 *
 * Lists every competency that has one or more assignments for the
 * current business. Managers can mark an in-progress assignment
 * complete (mints a Certificate of Competency in the existing
 * certificates collection), or unassign it.
 */

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Users,
  BadgeCheck,
  Clock3,
  Search,
  ChevronDown,
  ChevronRight,
  X,
  CheckCircle2,
  Trash2,
  RefreshCcw,
} from "lucide-react";
import AppShell from "../AppShell";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import {
  competencyCategories,
} from "../data/competencies";
import AssignCompetencyModal from "../components/AssignCompetencyModal";
import {
  PageHeader,
  Button,
  ErrorBanner,
  EmptyState,
} from "../../components/primitives";

const catalog = competencyCategories.flatMap((cat) =>
  cat.modules.map((m) => ({
    id: m.id,
    name: m.name,
    category_id: cat.id,
    category_name: cat.name,
  }))
);
const catalogById = Object.fromEntries(catalog.map((c) => [c.id, c]));

const statusStyles = {
  assigned: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const statusLabel = {
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
};

export default function Competencies() {
  const { business } = useAuth();
  const isDemo = business?.is_demo;

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [assignFor, setAssignFor] = useState(null);
  const [completeFor, setCompleteFor] = useState(null);
  const [scoreInput, setScoreInput] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await api.get("/competencies/assignments");
      setAssignments(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group by competency_id
  const grouped = useMemo(() => {
    const map = {};
    for (const a of assignments) {
      const cid = a.competency_id;
      if (!map[cid]) {
        map[cid] = {
          competency_id: cid,
          competency_name: a.competency_name,
          category_id: a.category_id || catalogById[cid]?.category_id || "",
          category_name:
            catalogById[cid]?.category_name || a.category_id || "General",
          items: [],
        };
      }
      map[cid].items.push(a);
    }
    return Object.values(map).sort((a, b) =>
      a.competency_name.localeCompare(b.competency_name)
    );
  }, [assignments]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return grouped;
    return grouped.filter(
      (g) =>
        g.competency_name.toLowerCase().includes(q) ||
        g.items.some((i) =>
          (i.team_member_name || "").toLowerCase().includes(q)
        )
    );
  }, [grouped, query]);

  const totals = useMemo(() => {
    const t = { assigned: 0, in_progress: 0, completed: 0 };
    for (const a of assignments) {
      t[a.status] = (t[a.status] || 0) + 1;
    }
    t.total = assignments.length;
    return t;
  }, [assignments]);

  async function completeAssignment() {
    if (!completeFor) return;
    setBusy(true);
    setError("");
    try {
      await api.post(
        `/competencies/assignments/${completeFor.id}/complete`,
        { score: Math.max(0, Math.min(100, Number(scoreInput) || 0)) }
      );
      setCompleteFor(null);
      setScoreInput("");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function unassign(a) {
    if (!window.confirm(`Unassign ${a.competency_name} from ${a.team_member_name}?`)) return;
    try {
      await api.del(`/competencies/assignments/${a.id}`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <AppShell>
      <PageHeader
        testid="competencies-page"
        kicker="Competency management"
        title="Every regulated capability, tracked."
        description="Assign competencies from the CanniComply library to individual staff members, monitor progress, and mint a Certificate of Competency the moment a pass mark is met."
        action={
          <Button
            variant="secondary"
            icon={RefreshCcw}
            onClick={load}
            data-testid="competencies-refresh"
          >
            Refresh
          </Button>
        }
      />

      {isDemo && (
        <div className="mb-5 rounded-lg border border-[#D4A65A]/30 bg-[#FDF6E7] px-4 py-3 text-[13px] text-[#8C6B33]">
          You're in demo mode — assignments below are illustrative and cannot
          be modified. Create your own workspace to run the workflow end-to-end.
        </div>
      )}

      <ErrorBanner error={error} className="mb-5" />

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="competencies-stats">
        <Stat label="Competencies active" value={grouped.length} icon={Sparkles} />
        <Stat label="Assignments total" value={totals.total} icon={Users} />
        <Stat label="In progress" value={(totals.assigned || 0) + (totals.in_progress || 0)} icon={Clock3} tone="gold" />
        <Stat label="Completed" value={totals.completed || 0} icon={BadgeCheck} tone="emerald" />
      </div>

      {/* Search */}
      <div className="mb-6">
        <label className="relative block max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search competency or staff member…"
            data-testid="competencies-search"
            className="w-full pl-9 pr-3 py-2.5 text-[13px] rounded-lg border border-slate-200 bg-white focus:border-[#D4A65A]/60 focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
          />
        </label>
      </div>

      {/* Groups */}
      {!loading && filtered.length === 0 && (
        <EmptyState
          icon={Sparkles}
          title="No competency assignments yet"
          description={
            <>
              Head to the <a href="/app/dashboard" className="font-semibold text-[#8C6B33] underline">dashboard Competency Library</a>{" "}
              and click <b>Assign</b> on a module to get started.
            </>
          }
        />
      )}

      <div className="space-y-6" data-testid="competencies-groups">
        {filtered.map((g, i) => {
          const isCollapsed = collapsed[g.competency_id];
          const total = g.items.length;
          const done = g.items.filter((x) => x.status === "completed").length;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <motion.section
              key={g.competency_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.03 }}
              className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
              data-testid={`competency-group-${g.competency_id}`}
            >
              <div
                className="w-full flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-slate-50 transition-colors"
                data-testid={`competency-group-row-${g.competency_id}`}
              >
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((c) => ({
                      ...c,
                      [g.competency_id]: !c[g.competency_id],
                    }))
                  }
                  className="flex-1 min-w-0 flex items-center gap-4 text-left rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/40"
                  data-testid={`competency-group-toggle-${g.competency_id}`}
                  aria-expanded={!isCollapsed}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="font-display text-[16px] md:text-[17px] font-semibold text-slate-900 tracking-tight">
                        {g.competency_name}
                      </h3>
                      <span className="text-[10.5px] font-mono text-slate-400">
                        {g.category_name}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-4 text-[11.5px] text-slate-500">
                      <span className="tabular-nums">
                        <b className="text-slate-900">{done}</b> / {total} completed
                      </span>
                      <span className="hidden sm:inline text-slate-300">·</span>
                      <span className="tabular-nums">{pct}% progress</span>
                    </div>
                  </div>
                  <span className="text-slate-400 shrink-0">
                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAssignFor({
                      id: g.competency_id,
                      name: g.competency_name,
                      category_id: g.category_id,
                      version: g.items[0]?.version || "1.0",
                      pass_mark: g.items[0]?.pass_mark || 80,
                    });
                  }}
                  data-testid={`competency-group-assign-${g.competency_id}`}
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#0B1220] hover:bg-[#131c31] text-white text-[11.5px] font-semibold px-3 py-1.5 shrink-0"
                >
                  Assign more
                </button>
              </div>

              {!isCollapsed && (
                <ul className="divide-y divide-slate-100">
                  {g.items.map((a) => (
                    <li
                      key={a.id}
                      className="px-5 md:px-6 py-3.5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
                      data-testid={`competency-assignment-${a.id}`}
                    >
                      <span className="h-9 w-9 rounded-full bg-[#0B1220] text-[#D4A65A] flex items-center justify-center text-[11px] font-semibold shrink-0">
                        {initials(a.team_member_name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] text-slate-900 font-semibold tracking-tight truncate">
                          {a.team_member_name}
                        </div>
                        <div className="text-[11.5px] text-slate-500 truncate">
                          {a.team_member_role || "Team member"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold ${statusStyles[a.status] || statusStyles.assigned}`}
                        >
                          {statusLabel[a.status] || a.status}
                        </span>
                        {a.status === "completed" && a.score != null && (
                          <span className="text-[11px] text-slate-600 font-mono tabular-nums">
                            {a.score}%
                          </span>
                        )}
                        <span className="text-[10.5px] font-mono text-slate-400">
                          v{a.version || "1.0"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 md:ml-3 shrink-0">
                        {a.status !== "completed" && (
                          <button
                            onClick={() => {
                              setCompleteFor(a);
                              setScoreInput(String(a.pass_mark || 80));
                            }}
                            disabled={isDemo}
                            data-testid={`competency-mark-complete-${a.id}`}
                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11.5px] font-semibold px-3 py-1.5"
                          >
                            <CheckCircle2 size={12} /> Mark competent
                          </button>
                        )}
                        {a.status === "completed" && a.cert_id && (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11.5px] font-semibold px-3 py-1.5"
                            data-testid={`competency-cert-${a.cert_id}`}
                          >
                            <BadgeCheck size={12} /> {a.cert_id}
                          </span>
                        )}
                        <button
                          onClick={() => unassign(a)}
                          disabled={isDemo}
                          data-testid={`competency-unassign-${a.id}`}
                          title="Unassign"
                          className="text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          );
        })}
      </div>

      {/* Modals */}
      {assignFor && (
        <AssignCompetencyModal
          competency={assignFor}
          isDemo={isDemo}
          onClose={() => setAssignFor(null)}
          onAssigned={() => load()}
        />
      )}

      {completeFor && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
          onClick={() => !busy && setCompleteFor(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="competency-complete-modal"
          >
            <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
                  Confirm competency
                </div>
                <h3 className="mt-1 font-display text-lg font-semibold text-slate-900 leading-tight">
                  {completeFor.competency_name}
                </h3>
                <div className="text-[12px] text-slate-500 mt-1">
                  {completeFor.team_member_name} · {completeFor.team_member_role || "Team member"}
                </div>
              </div>
              <button
                onClick={() => setCompleteFor(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  Assessment score
                </span>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={scoreInput}
                    onChange={(e) => setScoreInput(e.target.value)}
                    data-testid="competency-complete-score"
                    className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 tabular-nums focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
                  />
                  <span className="text-[13px] text-slate-500">
                    % · pass mark {completeFor.pass_mark || 80}%
                  </span>
                </div>
              </label>
              <p className="text-[12.5px] text-slate-500 leading-relaxed">
                Confirming will mint a Certificate of Competency for{" "}
                <b>{completeFor.team_member_name}</b> and add it to your
                audit ledger.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setCompleteFor(null)}
                className="text-[12.5px] text-slate-500 hover:text-slate-800 px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={completeAssignment}
                disabled={busy || isDemo}
                data-testid="competency-complete-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-4 py-2 text-[13px] font-semibold"
              >
                {busy ? "Confirming…" : "Confirm & mint certificate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const toneCls =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "gold"
      ? "text-[#8C6B33]"
      : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.14em] text-slate-500 font-mono">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className={`mt-1 text-2xl font-display font-semibold tracking-tight tabular-nums ${toneCls}`}>
        {value}
      </div>
    </div>
  );
}

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}
